import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Plus,
  Edit,
  Trash2,
  Eye,
  X,
  CheckCircle2,
  UserCircle,
  Clock,
  AlertCircle,
  FileText,
  Workflow,
  Search,
  Filter
} from 'lucide-react';
import {
  fetchAllTemplates,
  fetchTemplate,
  deleteTemplate
} from '../../../services/workflowTemplateService.ts';
import { fetchApprovers } from '../../../services/approverService.ts';
import { getAllUsers } from '../../../services/userService.ts';
import { useNotify } from 'react-admin';
import { useActionPermission } from '../../../hooks/useActionPermission.ts';

interface ApprovalStep {
  id: string;
  stepNo: number;
  Name: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: Approver[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
}

interface Approver {
  id: string;
  name: string;
  role: string;
  email: string;
  userId?: string;
  title?: string;
  departmentTitle?: string;
  selectorType?: 'user' | 'departmentTitle';
}

interface ApprovalTemplate {
  id?: string;
  Name: string;
  docType: string;
  description: string;
  steps: ApprovalStep[];
  isActive: boolean;
  createdDate: string;
  createdBy: number;
  lastModified: string;
  usageCount: number;
  stepsLoaded?: boolean;
}

type ApproverLike = Approver | string | number | { id?: string | number; userId?: string | number; name?: string; userName?: string; role?: string; title?: string; email?: string; departmentTitle?: string; departmentTile?: string; selectorType?: 'user' | 'departmentTitle' };

const buildApproverMap = (pool: Approver[]) => new Map(pool.map(approver => [approver.id, approver]));

const normalizeApprover = (
  item: ApproverLike,
  poolMap: Map<string, Approver>,
  userNameMap: Map<string, string>
): Approver => {
  if (item && typeof item === 'object') {
    const raw = item as { id?: string | number; userId?: string | number; name?: string; userName?: string; role?: string; title?: string; email?: string; departmentTitle?: string; departmentTile?: string; selectorType?: 'user' | 'departmentTitle' };
    // Prefer userId over id for user lookup
    const userId = raw.userId != null ? String(raw.userId) : (raw.id != null ? String(raw.id) : '');
    const id = raw.id != null ? String(raw.id) : userId;
    
    // Try to get name from multiple sources
    const rawName = raw.name || raw.userName;
    const mappedName = userId ? userNameMap.get(userId) : undefined;
    
    const fromPool = (id ? poolMap.get(id) : undefined) || (userId ? poolMap.get(userId) : undefined);
    if (fromPool) {
      return {
        ...fromPool,
        id: id || fromPool.id,
        userId: userId || fromPool.userId,
        name: rawName || mappedName || fromPool.name,
        title: raw.title || fromPool.title,
        role: raw.role || fromPool.role,
        email: raw.email || fromPool.email,
        departmentTitle: raw.departmentTitle || raw.departmentTile || fromPool.departmentTitle || '',
        selectorType: raw.selectorType || fromPool.selectorType || 'user'
      };
    }
    return {
      id,
      userId,
      name: rawName || mappedName || id || '',
      title: raw.title || '',
      role: raw.role || '-',
      email: raw.email || '',
      departmentTitle: raw.departmentTitle || raw.departmentTile || '',
      selectorType: raw.selectorType || 'user'
    };
  }

  const id = item != null ? String(item) : '';
  const fromPool = id ? poolMap.get(id) : undefined;
  if (fromPool) {
    const mappedName = userNameMap.get(id);
    return {
      ...fromPool,
      userId: id,
      name: mappedName || fromPool.name
    };
  }
  return { id, userId: id, name: userNameMap.get(id) || id, role: '-', email: '', departmentTitle: '', selectorType: 'user' };
};

const normalizeApproverList = (
  items: ApproverLike[] | undefined,
  poolMap: Map<string, Approver>,
  userNameMap: Map<string, string>
): Approver[] => {
  if (!items || items.length === 0) return [];
  return items.map(item => normalizeApprover(item, poolMap, userNameMap));
};

const needsApproverHydration = (items: ApproverLike[] | undefined): boolean => {
  if (!items || items.length === 0) return false;
  return items.some(item => {
    if (!item) return true;
    if (typeof item !== 'object') return true;
    const raw = item as { id?: string | number; userId?: string | number; name?: string; userName?: string };
    // If we have userName but not name, we need to hydrate it
    if (raw.userName && !raw.name) return true;
    // Check if we have a display name that's not just a numeric ID
    const displayName = raw.name || raw.userName;
    if (!displayName) return true; // Need hydration if no name
    // Check if name looks like just a numeric ID (userId/id)
    if (/^\d+$/.test(String(displayName).trim())) return true;
    return false;
  });
};

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { can } = useActionPermission();

  const canCreateTemplate = can('create', { menu: 'QmsWorkflowTemplate' });
  const canEditTemplate = can('edit', { menu: 'QmsWorkflowTemplate' });
  const canDeleteTemplate = can('delete', { menu: 'QmsWorkflowTemplate' });
  // const templateUrl = 'QmsWorkflowTemplate';

  const [templates, setTemplates] = useState<ApprovalTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<ApprovalTemplate | null>(null);
  const [isDesignerOpen, setIsDesignerOpen] = useState(false);
  const [detailsLoadingId, setDetailsLoadingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDocType, setFilterDocType] = useState('all');
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [approversPool, setApproversPool] = useState<Approver[]>([]);
  const [userNameMap, setUserNameMap] = useState<Map<string, string>>(new Map());
  const [isLoadingApprovers, setIsLoadingApprovers] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ isOpen: boolean; action: (() => void) | null; message: string }>({
    isOpen: false,
    action: null,
    message: ''
  });

  const approverMap = useMemo(() => buildApproverMap(approversPool), [approversPool]);

  const getCreatedByLabel = (createdBy: number) => {
    const fromUserMap = userNameMap.get(String(createdBy));
    if (fromUserMap) return fromUserMap;
    const fromPool = approverMap.get(String(createdBy));
    return fromPool?.name || String(createdBy ?? '');
  };

  // Load templates with steps on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setIsLoading(true);
        const data = await fetchAllTemplates({ includeSteps: true });
        setTemplates(data.map(t => ({
          ...t,
          stepsLoaded: true,
          steps: (t.steps || []).map(s => ({
            ...s,
            id: s.id || `s${Math.random().toString(36).substr(2, 9)}`
          }))
        })));
      } catch (error) {
        console.error('Failed to load templates:', error);
        notify('Failed to load workflow templates', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [notify]);

  useEffect(() => {
    if (approversPool.length > 0) return;

    const loadApprovers = async () => {
      try {
        setIsLoadingApprovers(true);
        const data = await fetchApprovers();
        setApproversPool(data);
      } catch (error) {
        console.error('Failed to load approvers:', error);
        notify('Failed to load approvers', { type: 'error' });
      } finally {
        setIsLoadingApprovers(false);
      }
    };

    loadApprovers();
  }, [approversPool.length, notify]);

  // Load approvers on-demand when modal or view opens
  useEffect(() => {
    if (!showApproverModal && !isDesignerOpen) return;
    if (approversPool.length > 0) return;

    const loadApprovers = async () => {
      try {
        setIsLoadingApprovers(true);
        const data = await fetchApprovers();
        setApproversPool(data);
      } catch (error) {
        console.error('Failed to load approvers:', error);
        notify('Failed to load approvers', { type: 'error' });
      } finally {
        setIsLoadingApprovers(false);
      }
    };

    loadApprovers();
  }, [showApproverModal, isDesignerOpen, approversPool.length, notify]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await getAllUsers();
        const map = new Map<string, string>();
        users.forEach((user: any) => {
          const id = user?.id ?? user?.Id ?? user?.userId ?? user?.UserId;
          const name = user?.userName ?? user?.username ?? user?.name ?? user?.Name;
          if (id != null && name) {
            map.set(String(id), String(name));
          }
        });
        setUserNameMap(map);
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    // Trigger normalization whenever userNameMap updates, even if approversPool is empty
    // This ensures we use userNameMap to map userId -> userName
    const poolMap = buildApproverMap(approversPool);
    setTemplates(prev => {
      let anyChanged = false;
      const updated = prev.map(template => {
        if (!template.steps || template.steps.length === 0) return template;
        let stepsChanged = false;
        const updatedSteps = template.steps.map(step => {
          const rawApprovers = step.approvers as unknown as ApproverLike[];
          if (!needsApproverHydration(rawApprovers)) {
            return step;
          }
          stepsChanged = true;
          return {
            ...step,
            approvers: normalizeApproverList(rawApprovers, poolMap, userNameMap)
          };
        });
        if (!stepsChanged) return template;
        anyChanged = true;
        return { ...template, steps: updatedSteps };
      });
      return anyChanged ? updated : prev;
    });
  }, [approversPool, userNameMap]);

  useEffect(() => {
    // Trigger normalization whenever userNameMap updates, even if approversPool is empty
    // This ensures we use userNameMap to map userId -> userName
    const poolMap = buildApproverMap(approversPool);
    
    setSelectedTemplate(prev => {
      if (!prev) return prev;
      
      let changed = false;
      const updatedSteps = prev.steps.map(step => {
        const rawApprovers = step.approvers as unknown as ApproverLike[];
        if (!needsApproverHydration(rawApprovers)) {
          return step;
        }
        changed = true;
        return {
          ...step,
          approvers: normalizeApproverList(rawApprovers, poolMap, userNameMap)
        };
      });

      if (!changed) return prev;
      return { ...prev, steps: updatedSteps };
    });
  }, [approversPool, userNameMap]);

  // Filtered templates
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      const matchesSearch = (template.Name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (template.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDocType = filterDocType === 'all' || template.docType === filterDocType;
      return matchesSearch && matchesDocType;
    });
  }, [templates, searchTerm, filterDocType]);

  const ensureTemplateDetails = async (template: ApprovalTemplate) => {
    if (template.stepsLoaded) {
      return template;
    }

    if (!template.id) {
      throw new Error('Template ID is missing');
    }

    setDetailsLoadingId(template.id);
    try {
      const fullTemplate = await fetchTemplate(template.id);
      const mergedTemplate: ApprovalTemplate = {
        ...fullTemplate,
        stepsLoaded: true,
        steps: (fullTemplate.steps || []).map(s => ({
          ...s,
          id: s.id || `s${Math.random().toString(36).substr(2, 9)}`
        }))
      };
      setTemplates(prev => prev.map(t => (t.id === template.id ? mergedTemplate : t)));
      return mergedTemplate;
    } finally {
      setDetailsLoadingId(null);
    }
  };

  // Handle view template
  const handleViewTemplate = async (template: ApprovalTemplate) => {
    setSelectedTemplate(JSON.parse(JSON.stringify(template)));
    setIsDesignerOpen(true);

    try {
      const fullTemplate = await ensureTemplateDetails(template);
      setSelectedTemplate(JSON.parse(JSON.stringify(fullTemplate)));
    } catch (error) {
      console.error('Failed to load template details:', error);
      notify('Failed to load workflow details', { type: 'error' });
    }
  };

  // Handle edit template
  const handleEditTemplate = (template: ApprovalTemplate) => {
    if (!canEditTemplate) {
      notify('No permission to edit template', { type: 'warning' });
      return;
    }
    navigate(`/QmsWorkflowTemplate/${template.id}`);
  };

  // Handle create new template
  const handleCreateTemplate = () => {
    if (!canCreateTemplate) {
      notify('No permission to create template', { type: 'warning' });
      return;
    }
    navigate(`/QmsWorkflowTemplate/create`);
  };

  // Handle delete template
  const handleDeleteTemplate = (id: string) => {
    if (!canDeleteTemplate) {
      notify('No permission to delete template', { type: 'warning' });
      return;
    }
    setConfirmDialog({
      isOpen: true,
      message: 'Are you sure you want to delete this approval workflow template?',
      action: async () => {
        try {
          await deleteTemplate(id);
          setTemplates(templates.filter(t => t.id !== id));
          setConfirmDialog({ isOpen: false, action: null, message: '' });
          notify('Template deleted successfully', { type: 'success' });
        } catch (error) {
          console.error('Failed to delete template:', error);
          notify('Failed to delete template', { type: 'error' });
        }
      }
    });
  };



  // Add approver to step
  const handleAddApprover = (stepId: string, approver: Approver) => {
    if (selectedTemplate) {
      setSelectedTemplate({
        ...selectedTemplate,
        steps: selectedTemplate.steps.map(s =>
          s.id === stepId ? { ...s, approvers: [...s.approvers, approver] } : s
        )
      });
    }
  };

  return (
    <div className="w-full h-full overflow-auto flex flex-col bg-transparent">
      {/* Header+搜索+筛选合并卡片 */}
      <div className="pb-3 flex-shrink-0">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
            <div>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">Workflow Templates</h2>
              <p className="text-sm text-slate-500 mt-1">Create and manage document approval workflow templates</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="inline-flex items-center h-8 px-3 rounded-full bg-slate-100 text-slate-700 text-sm font-bold">
                Total: {isLoading ? '...' : filteredTemplates.length}
              </span>
              {canCreateTemplate && (
                <button
                  onClick={handleCreateTemplate}
                  className="inline-flex items-center gap-2 px-4 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
                >
                  <Plus className="w-4 h-4" />
                  New Template
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search template name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div className="flex items-center gap-3 flex-wrap mt-2 md:mt-0">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600 text-sm">Filter:</span>
              </div>
              <select
                value={filterDocType}
                onChange={(e) => setFilterDocType(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white"
              >
                <option value="all">All Document Types</option>
                <option value="SOP">SOP</option>
                <option value="WI">Work Instruction</option>
                <option value="FORM">Form</option>
                <option value="POLICY">Policy</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 pb-4 overflow-auto">
        <div className="min-h-full rounded-2xl border border-gray-200 bg-white shadow-[0_6px_18px_rgba(15,23,42,0.05)] overflow-hidden">
          {isLoading ? (
            <div className="py-16 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900 mx-auto"></div>
              <p className="text-gray-500 mt-3 text-sm">Loading templates...</p>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead className="bg-slate-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-slate-700 font-bold text-sm w-[280px]">Template Name</th>
                  <th className="px-3 py-3 text-left text-slate-700 font-bold text-sm w-[100px]">Doc Type</th>
                  <th className="px-3 py-3 text-center text-slate-700 font-bold text-sm w-[80px]">Steps</th>
                  <th className="px-3 py-3 text-left text-slate-700 font-bold text-sm">Approvers Overview</th>
                  <th className="px-3 py-3 text-left text-slate-700 font-bold text-sm w-[130px]">Created By</th>
                  <th className="px-3 py-3 text-center text-slate-700 font-bold text-sm w-[160px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTemplates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center">
                      <Workflow className="size-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">No templates found</p>
                      <p className="text-sm text-gray-400 mt-1 mb-4">Try adjusting search criteria or create a new template</p>
                      <button
                        onClick={handleCreateTemplate}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
                      >
                        <Plus className="size-4" />
                        New Template
                      </button>
                    </td>
                  </tr>
                ) : (
                  filteredTemplates.map(template => (
                    <tr
                      key={template.id}
                      className="hover:bg-slate-50 even:bg-[#fbfdff] transition-colors text-sm"
                    >
                      {/* Name + description */}
                      <td className="px-6 py-3">
                        <div className="font-semibold text-slate-800">{template.Name}</div>
                        {template.description && (
                          <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[240px]">{template.description}</div>
                        )}
                      </td>

                      {/* Doc Type */}
                      <td className="px-3 py-3">
                        <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-semibold whitespace-nowrap">
                          {template.docType}
                        </span>
                      </td>

                      {/* Steps count */}
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-slate-700 font-medium">
                          <FileText className="size-3.5 text-gray-400" />
                          {template.stepsLoaded ? template.steps.length : '--'}
                        </span>
                      </td>

                      {/* Approvers overview */}
                      <td className="px-3 py-3">
                        <div className="space-y-1">
                          {!template.stepsLoaded ? (
                            <span className="text-xs text-gray-400">Load on view</span>
                          ) : template.steps.length === 0 ? (
                            <span className="text-xs text-gray-400">No steps</span>
                          ) : (
                            template.steps.slice(0, 2).map(step => (
                              <div key={step.id} className="flex items-center gap-1.5">
                                <span className="text-xs font-semibold text-gray-500 w-12 shrink-0">Step {step.stepNo}:</span>
                                <div className="flex flex-wrap gap-1">
                                  {step.approvers.length === 0 ? (
                                    <span className="text-xs text-gray-400 italic">Not assigned</span>
                                  ) : (
                                    step.approvers.slice(0, 3).map(a => (
                                      <span key={a.id} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-700">
                                        <UserCircle className="size-3" />
                                        {(a as any).selectorType === 'departmentTitle'
                                          ? ((a as any).departmentTitle || a.name)
                                          : (a.name || (a as any).userName || a.id)}
                                      </span>
                                    ))
                                  )}
                                  {step.approvers.length > 3 && (
                                    <span className="text-xs text-gray-400">+{step.approvers.length - 3} more</span>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                          {template.stepsLoaded && template.steps.length > 2 && (
                            <span className="text-xs text-gray-400">+{template.steps.length - 2} more steps</span>
                          )}
                        </div>
                      </td>

                      {/* Created By */}
                      <td className="px-3 py-3">
                        <div className="text-sm text-gray-700">{getCreatedByLabel(template.createdBy)}</div>
                        <div className="text-xs text-gray-400">{template.createdDate}</div>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handleViewTemplate(template)}
                            className="inline-flex items-center px-2 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
                          >
                            <Eye className="size-3 mr-1" />
                            View
                          </button>
                          {canEditTemplate && (
                            <button
                              onClick={() => handleEditTemplate(template)}
                              className="inline-flex items-center px-2 py-1 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 rounded-md transition-colors"
                            >
                              <Edit className="size-3 mr-1" />
                              Edit
                            </button>
                          )}
                          {canDeleteTemplate && (
                            <button
                              onClick={() => template.id && handleDeleteTemplate(template.id)}
                              className="inline-flex items-center px-2 py-1 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 className="size-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Template View Modal */}
      {isDesignerOpen && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-extrabold text-slate-900">{selectedTemplate.Name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-semibold">{selectedTemplate.docType}</span>
                    <span className="text-xs text-gray-500">{selectedTemplate.steps.length} step{selectedTemplate.steps.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsDesignerOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Description */}
              {selectedTemplate.description && (
                <p className="text-sm text-gray-600 mb-5">{selectedTemplate.description}</p>
              )}

              {/* Steps */}
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Approval Steps</h3>
              {detailsLoadingId === selectedTemplate.id && !selectedTemplate.stepsLoaded ? (
                <div className="text-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
                  <p className="text-gray-500 mt-3 text-sm">Loading workflow details...</p>
                </div>
              ) : selectedTemplate.steps.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl">
                  <Workflow className="size-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">No approval steps configured</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedTemplate.steps.map((step, index) => (
                    <div key={step.id}>
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        {/* Step header bar */}
                        <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-gray-200">
                          <div className="w-7 h-7 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {step.stepNo}
                          </div>
                          <span className="font-semibold text-slate-800 text-sm">{step.Name}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            step.stepType === 'parallel' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {step.stepType === 'parallel' ? 'Parallel' : 'Single'}
                          </span>
                          {step.timeLimit && (
                            <span className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500">
                              <Clock className="size-3" />{step.timeLimit}d
                            </span>
                          )}
                        </div>
                        {/* Step body */}
                        <div className="p-4">
                          {step.description && (
                            <p className="text-xs text-gray-500 mb-3">{step.description}</p>
                          )}
                          {/* Approvers grouped */}
                          <div className="space-y-2">
                            {step.approvers.some(a => (a as any).selectorType === 'departmentTitle') && (
                              <div>
                                <div className="text-xs font-semibold text-purple-600 mb-1.5 uppercase tracking-wide">Department Titles</div>
                                <div className="flex flex-wrap gap-2">
                                  {step.approvers.filter(a => (a as any).selectorType === 'departmentTitle').map(a => (
                                    <span key={a.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                                      <UserCircle className="size-3.5 text-purple-500" />
                                      <span className="font-medium text-gray-900">{(a as any).departmentTitle || a.name}</span>
                                      <span className="text-xs text-purple-500">Dynamic</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {step.approvers.some(a => (a as any).selectorType !== 'departmentTitle') && (
                              <div>
                                <div className="text-xs font-semibold text-blue-600 mb-1.5 uppercase tracking-wide">Persons</div>
                                <div className="flex flex-wrap gap-2">
                                  {step.approvers.filter(a => (a as any).selectorType !== 'departmentTitle').map(a => (
                                    <span key={a.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                                      <UserCircle className="size-3.5 text-blue-500" />
                                      <div>
                                        <div className="font-medium text-gray-900 text-xs">{a.name || (a as any).userName || a.id}</div>
                                        <div className="text-[10px] text-gray-400">{a.departmentTitle || a.title || a.role || '-'}</div>
                                      </div>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {step.approvers.length === 0 && (
                              <span className="text-xs text-gray-400 italic">No approvers assigned</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {index < selectedTemplate.steps.length - 1 && (
                        <div className="flex justify-center py-1.5">
                          <div className="w-0.5 h-4 bg-gray-200 rounded"></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              {canEditTemplate && (
                <button
                  onClick={() => selectedTemplate.id && handleEditTemplate(selectedTemplate)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold"
                >
                  <Edit className="size-4" />
                  Edit Template
                </button>
              )}
              <button
                onClick={() => setIsDesignerOpen(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-[10px] hover:bg-gray-50 transition-colors text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approver Selection Modal */}
      {showApproverModal && currentStepId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Select Approver</h3>
                <button
                  onClick={() => {
                    setShowApproverModal(false);
                    setCurrentStepId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {isLoadingApprovers ? (
                <div className="text-center py-12 text-gray-600">
                  <div className="inline-block mb-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                  <p>Loading approvers...</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                  {approversPool.map((approver: Approver) => {
                  const currentStep = selectedTemplate?.steps.find(s => s.id === currentStepId);
                  const isAlreadyAdded = currentStep?.approvers.some(a => a.id === approver.id);
                  
                  return (
                    <button
                      key={approver.id}
                      onClick={() => {
                        if (!isAlreadyAdded) {
                          handleAddApprover(currentStepId, approver);
                        }
                      }}
                      disabled={isAlreadyAdded}
                      className={`p-4 border rounded-lg text-left transition-colors ${
                        isAlreadyAdded
                          ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                          : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <UserCircle className="size-10 text-gray-400" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{approver.name || (approver as any).userName || approver.id}</div>
                          <div className="text-sm text-gray-600">{approver.departmentTitle || approver.title || approver.role || '-'}</div>
                          <div className="text-xs text-gray-500">{approver.email}</div>
                        </div>
                        {isAlreadyAdded && (
                          <CheckCircle2 className="size-5 text-green-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowApproverModal(false);
                  setCurrentStepId(null);
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <AlertCircle className="size-5 text-red-600" />
                <h3 className="text-base font-bold text-gray-900">Confirm Delete</h3>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700">{confirmDialog.message}</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setConfirmDialog({ isOpen: false, action: null, message: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-[10px] hover:bg-gray-50 transition-colors text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDialog.action?.()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-[10px] hover:bg-red-700 transition-colors text-sm font-bold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateList;
